import React, { useState, useEffect } from 'react';
import { useSettings } from './context';
import { S } from '../../styles';
import { API, authHeaders } from '../../utils/helpers';

// ── Fittings settings (3 Oct) ──────────────────────────────────────────────────
//
// Under Online Booking. Everything here is saved with the page's Save button,
// as part of settings.booking:
//   - (each booking package is marked Drop-off or Appointment, with a length,
//     on its own card in SettingsBooking since 4 Oct: enabledItems[].kind / .minutes)
//   - settings.booking.fittings: fitters and their weekly hours, fit stations,
//     the booking rules, the questions on the booking page and the deposit.
// The rules that use them are in server/helpers/fittings.js.

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DEFAULT_QUESTIONS = [
  { id: 'height', label: 'Height' },
  { id: 'riding', label: 'How do you ride?' },
  { id: 'pain', label: 'Any pain or problems on the bike?' },
];
// A new fitter starts on the shop's usual week, Tuesday to Saturday, 10 to 6.
const NEW_HOURS = [null, null, { start: '10:00', end: '18:00' }, { start: '10:00', end: '18:00' }, { start: '10:00', end: '18:00' }, { start: '10:00', end: '18:00' }, { start: '10:00', end: '18:00' }];

// '18:00' -> '6', '09:30' -> '9:30'
const shortTime = (t) => {
  const [h, m] = String(t || '').split(':').map(Number);
  if (!Number.isFinite(h)) return '';
  const h12 = ((h + 11) % 12) + 1;
  return m ? `${h12}:${String(m).padStart(2, '0')}` : String(h12);
};

export default function SettingsFittings() {
  const { T, isDark, settings, setBooking, allStatuses = [] } = useSettings();
  const b = settings.booking || {};
  const f = b.fittings || {};
  const fitters = Array.isArray(f.fitters) ? f.fitters : [];
  const questions = Array.isArray(f.questions) ? f.questions : DEFAULT_QUESTIONS;
  const setFit = (patch) => setBooking({ fittings: { ...f, ...patch } });
  const [candidates, setCandidates] = useState([]);
  const [editing, setEditing] = useState(null);     // userId whose hours are open
  const [adding, setAdding] = useState('');

  useEffect(() => {
    fetch(`${API}/book/fit/staff/candidates`, { credentials: 'include', headers: authHeaders() })
      .then(r => r.json()).then(d => setCandidates(Array.isArray(d.users) ? d.users : [])).catch(() => {});
  }, []);

  const nameOf = (id) => (candidates.find(c => String(c.userId) === String(id)) || {}).name || `User ${id}`;
  const linkedOf = (id) => !!(candidates.find(c => String(c.userId) === String(id)) || {}).linked;
  const packages = b.enabledItems || [];
  const setFitter = (userId, patch) => setFit({ fitters: fitters.map(x => String(x.userId) === String(userId) ? { ...x, ...patch } : x) });
  const setDay = (fitter, d, val) => {
    const hours = Array.from({ length: 7 }, (_, i) => (Array.isArray(fitter.hours) ? fitter.hours[i] : null) || null);
    hours[d] = val;
    setFitter(fitter.userId, { hours });
  };

  const inputStyle = { background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 6, padding: '6px 10px', color: T.text, fontSize: 13, fontFamily: 'Arial, sans-serif', outline: 'none', boxSizing: 'border-box' };
  const num = (key, fallback, min, max) => (
    <input type="number" min={min} max={max} value={f[key] ?? fallback}
      onChange={e => setFit({ [key]: e.target.value === '' ? '' : Math.max(min, Math.min(max, parseInt(e.target.value, 10) || 0)) })}
      style={{ ...S.thresholdInput, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text }} />
  );
  const sub = { fontSize: 12, color: T.textSub, lineHeight: 1.5 };
  const section = (n, title, desc) => (
    <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${T.cardBorder}` }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{n} · {title}</div>
      {desc ? <div style={{ ...sub, marginTop: 3, marginBottom: 10 }}>{desc}</div> : <div style={{ height: 8 }} />}
    </div>
  );
  const tile = isDark ? '#1e2a40' : '#f4f4ef';
  const serviceStatus = (allStatuses.find(st => String(st.id) === String(b.onlineBookingStatusId || '')) || {}).name || '';
  const serviceCancelStatus = (allStatuses.find(st => String(st.id) === String(b.cancellationStatusId || '')) || {}).name || '';
  const appointments = packages.filter(p => p.kind === 'appointment');
  const bookable = fitters.filter(x => x.bookable !== false && linkedOf(x.userId));

  return (
    <div style={{ ...S.settingsCard, background: T.card, border: `1px solid ${T.cardBorder}` }}>
      <div style={{ ...S.settingsCardTitle, color: T.text }}>Fittings</div>
      <div style={{ ...S.settingsCardDesc, color: T.textSub }}>
        Appointments booked to a time and a fitter, for fits and anything else that needs one person for a set time.
        {appointments.length > 0 && bookable.length > 0
          ? <span style={{ color: '#2ecc71' }}> {appointments.length} package{appointments.length === 1 ? '' : 's'} bookable by appointment with {bookable.length} fitter{bookable.length === 1 ? '' : 's'}.</span>
          : <span> Off until a package above is marked Appointment and a fitter is added.</span>}
      </div>

      {section(1, 'Fitters and their hours', 'Who can be booked, and when. Each is a Spinboard login linked to a Lightspeed employee, so the ticket is assigned to them.')}
      <div style={{ display: 'grid', gap: 8 }}>
        {fitters.map(fitter => {
          const hours = Array.isArray(fitter.hours) ? fitter.hours : [];
          const linked = linkedOf(fitter.userId);
          const open = String(editing) === String(fitter.userId);
          return (
            <div key={fitter.userId} style={{ background: tile, borderRadius: 8, padding: '10px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{nameOf(fitter.userId)}</div>
                  <div style={{ fontSize: 11, color: linked ? T.textSub : '#e67e22' }}>
                    {linked ? 'Linked to Lightspeed' : 'Not linked to a Lightspeed employee: link it in Users, or this fitter cannot be booked'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {DAYS.map((d, i) => (
                    <span key={d} style={{ textAlign: 'center', minWidth: 38, fontSize: 11, padding: '3px 2px', borderRadius: 6,
                      background: hours[i] ? (isDark ? '#F4622A22' : '#F4622A18') : 'transparent', color: hours[i] ? T.text : T.textSub,
                      border: `1px solid ${hours[i] ? '#F4622A55' : T.inputBorder}` }}>
                      <div style={{ fontWeight: 700 }}>{d}</div>
                      <div>{hours[i] ? `${shortTime(hours[i].start)}–${shortTime(hours[i].end)}` : 'Off'}</div>
                    </span>
                  ))}
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.text, cursor: 'pointer' }}>
                  <input type="checkbox" checked={fitter.bookable !== false} onChange={e => setFitter(fitter.userId, { bookable: e.target.checked })}
                    style={{ accentColor: '#F4622A', width: 15, height: 15 }} />
                  Bookable
                </label>
                <button type="button" onClick={() => setEditing(open ? null : fitter.userId)}
                  style={{ ...S.btn(false), fontSize: 12, padding: '4px 10px', color: T.textSub, borderColor: T.border }}>{open ? 'Done' : 'Edit hours'}</button>
                <button type="button" title="Remove this fitter" onClick={() => setFit({ fitters: fitters.filter(x => x.userId !== fitter.userId) })}
                  style={{ background: 'transparent', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 15 }}>×</button>
              </div>
              {open && (
                <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
                  {DAYS.map((d, i) => (
                    <div key={d} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: 80, fontSize: 13, color: T.text, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!hours[i]} onChange={e => setDay(fitter, i, e.target.checked ? { start: '10:00', end: '18:00' } : null)}
                          style={{ accentColor: '#F4622A', width: 15, height: 15 }} />
                        {d}
                      </label>
                      {hours[i] ? (
                        <>
                          <input type="time" step="900" value={hours[i].start} onChange={e => setDay(fitter, i, { ...hours[i], start: e.target.value })} style={inputStyle} />
                          <span style={{ color: T.textSub, fontSize: 12 }}>to</span>
                          <input type="time" step="900" value={hours[i].end} onChange={e => setDay(fitter, i, { ...hours[i], end: e.target.value })} style={inputStyle} />
                        </>
                      ) : <span style={{ fontSize: 12, color: T.textSub }}>Off</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={adding} onChange={e => setAdding(e.target.value)} style={inputStyle}>
            <option value="">Add a fitter…</option>
            {candidates.filter(c => !fitters.some(x => String(x.userId) === String(c.userId))).map(c => (
              <option key={c.userId} value={c.userId}>{c.name}{c.linked ? '' : ' (not linked to Lightspeed)'}</option>
            ))}
          </select>
          <button type="button" disabled={!adding}
            onClick={() => { setFit({ fitters: [...fitters, { userId: Number(adding), bookable: true, hours: NEW_HOURS }] }); setAdding(''); }}
            style={{ ...S.btn(false), fontSize: 12, color: adding ? '#F4622A' : T.textSub, borderColor: adding ? '#F4622A55' : T.border }}>+ Add</button>
        </div>
        <div style={sub}>
          Days off and holidays: <strong>Block time</strong> on the Fittings calendar (Bookings, Fittings). Days the shop is closed for drop-offs are never bookable.
        </div>
      </div>

      {section(2, 'Stations')}
      <div style={S.thresholdRow}>
        <span style={{ ...S.thresholdLabel, color: T.text }}>Fit stations</span>
        {num('stations', 1, 1, 10)}
        <span style={{ ...S.thresholdUnit, color: T.textSub }}>With one station, only one fit can run at a time, even with two fitters in.</span>
      </div>

      {section(3, 'Booking rules')}
      <div style={S.thresholdRow}>
        <span style={{ ...S.thresholdLabel, color: T.text }}>Start times every</span>
        <select value={f.startEvery ?? 30} onChange={e => setFit({ startEvery: parseInt(e.target.value, 10) })} style={inputStyle}>
          {[15, 30, 60].map(m => <option key={m} value={m}>{m} min</option>)}
        </select>
      </div>
      <div style={S.thresholdRow}>
        <span style={{ ...S.thresholdLabel, color: T.text }}>Buffer between appointments</span>
        <select value={f.buffer ?? 15} onChange={e => setFit({ buffer: parseInt(e.target.value, 10) })} style={inputStyle}>
          {[0, 5, 10, 15, 20, 30].map(m => <option key={m} value={m}>{m} min</option>)}
        </select>
      </div>
      <div style={S.thresholdRow}>
        <span style={{ ...S.thresholdLabel, color: T.text }}>Book at least</span>
        {num('noticeHours', 24, 0, 336)}
        <span style={{ ...S.thresholdUnit, color: T.textSub }}>hours ahead, up to</span>
        {num('windowDays', 30, 7, 120)}
        <span style={{ ...S.thresholdUnit, color: T.textSub }}>days out</span>
      </div>
      <div style={S.thresholdRow}>
        <span style={{ ...S.thresholdLabel, color: T.text }}>Customers can move or cancel until</span>
        {num('freeCancelHours', 24, 0, 168)}
        <span style={{ ...S.thresholdUnit, color: T.textSub }}>hours before (later than that, they call)</span>
      </div>

      <div style={S.thresholdRow}>
        <span style={{ ...S.thresholdLabel, color: T.text }}>Status for new fittings</span>
        <select value={f.statusId || ''} onChange={e => setFit({ statusId: e.target.value || null })} style={{ ...inputStyle, flex: 1 }}>
          <option value="">Same as service bookings{serviceStatus ? ` (${serviceStatus})` : ''}</option>
          {/* "Done & Paid" closes a ticket in Lightspeed for good; never a status for a new booking. */}
          {allStatuses.filter(st => { const n = (st.name || '').toLowerCase(); return !(n.includes('done') && n.includes('paid')); })
            .map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
        </select>
      </div>
      <div style={S.thresholdRow}>
        <span style={{ ...S.thresholdLabel, color: T.text }}>Status on cancellation</span>
        <select value={f.cancelStatusId || ''} onChange={e => setFit({ cancelStatusId: e.target.value || null })} style={{ ...inputStyle, flex: 1 }}>
          <option value="">Same as service bookings{serviceCancelStatus ? ` (${serviceCancelStatus})` : ' (not set)'}</option>
          {allStatuses.filter(st => { const n = (st.name || '').toLowerCase(); return !(n.includes('done') && n.includes('paid')); })
            .map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
        </select>
      </div>
      <div style={{ ...sub, marginTop: -4 }}>The Lightspeed status a fit's ticket starts in, and where it moves when the fit is canceled by the shop or the customer. The ticket is never deleted.</div>

      {section(4, 'Note on the booking page', 'Shown under the packages when a customer picks a fitting, instead of the service note. Leave it empty for none.')}
      <textarea rows={2} value={f.disclaimerText || ''} placeholder="e.g. Please wear your riding shoes and bring your pedals."
        onChange={e => setFit({ disclaimerText: e.target.value })}
        style={{ ...inputStyle, width: '100%', resize: 'vertical', lineHeight: 1.5 }} />

      {section(5, 'Questions and deposit')}
      <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 6 }}>Questions on the booking page</div>
      <div style={{ display: 'grid', gap: 6, marginBottom: 8 }}>
        {questions.map((q, i) => (
          <div key={q.id} style={{ display: 'flex', gap: 6 }}>
            <input value={q.label} placeholder="Question"
              onChange={e => setFit({ questions: questions.map((x, j) => j === i ? { ...x, label: e.target.value } : x) })}
              style={{ ...inputStyle, flex: 1 }} />
            <button type="button" title="Remove" onClick={() => setFit({ questions: questions.filter((_, j) => j !== i) })}
              style={{ background: 'transparent', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 15 }}>×</button>
          </div>
        ))}
        <button type="button" onClick={() => setFit({ questions: [...questions, { id: `q${Date.now().toString(36)}`, label: '' }] })}
          style={{ ...S.btn(false), fontSize: 12, color: T.textSub, borderColor: T.border, justifySelf: 'start' }}>+ Add a question</button>
        <div style={sub}>Optional for the customer. The answers go on the Lightspeed ticket and the fit's details.</div>
      </div>
      <div style={S.thresholdRow}>
        <span style={{ ...S.thresholdLabel, color: T.text }}>Deposit</span>
        <span style={{ color: T.textSub }}>$</span>
        <input type="number" min="0" step="1" value={f.deposit ?? ''} placeholder="Full price"
          onChange={e => setFit({ deposit: e.target.value === '' ? null : Math.max(0, Number(e.target.value)) })}
          style={{ ...S.thresholdInput, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text }} />
        <span style={{ ...S.thresholdUnit, color: T.textSub }}>per appointment</span>
      </div>
      <div style={sub}>
        {(b.paymentMode || 'off') === 'off'
          ? 'Payment at booking is off above, so no deposit is taken. Turn it on to hold fits with a deposit.'
          : 'Taken the same way as service bookings (Payment at booking, above). Leave it blank to take the full package price. Refunded automatically if the customer cancels in time.'}
      </div>
    </div>
  );
}
